import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { Plus, Trash2, Check, X, Undo2, Save } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import ModulePageHeading from '../../components/ModulePageHeading';
import { resolveSettingsNav } from '../../navigation/settingsNavigation';

const STORAGE_KEY_PREFIX = 'db_structure_';

function loadStored(tableName) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_PREFIX + tableName);
    if (!raw) return { pending: [], applied: [] };
    const data = JSON.parse(raw);
    return { pending: data.pending || [], applied: data.applied || [] };
  } catch {
    return { pending: [], applied: [] };
  }
}

function saveStored(tableName, pending, applied) {
  try {
    localStorage.setItem(STORAGE_KEY_PREFIX + tableName, JSON.stringify({ pending, applied }));
  } catch (e) {
    console.warn('Failed to save structure changes', e);
  }
}

export default function DbStructure() {
  const { tableName } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [structure, setStructure] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState([]);
  const [applied, setApplied] = useState([]);
  const [showAddColumn, setShowAddColumn] = useState(false);
  const [addForm, setAddForm] = useState({ columnName: '', columnType: 'VARCHAR(255)', isNullable: true, columnDefault: '' });

  const loadStructure = useCallback(() => {
    if (!tableName) return;
    setLoading(true);
    api.get(`/settings/db/tables/${tableName}/structure`)
      .then((res) => setStructure(res.data.data || []))
      .catch((err) => {
        toast.error(err.response?.data?.error || 'Failed to load structure');
        if (err.response?.status === 401) navigate('/settings/db');
      })
      .finally(() => setLoading(false));
  }, [tableName, navigate]);

  useEffect(() => {
    if (user?.role !== 'ADMIN' && user?.role !== 'SUPER_ADMIN') {
      navigate('/settings');
      return;
    }
    if (!tableName) {
      navigate('/settings/db');
      return;
    }
    const stored = loadStored(tableName);
    setPending(stored.pending);
    setApplied(stored.applied);
    loadStructure();
  }, [tableName, user?.role, navigate, loadStructure]);

  useEffect(() => {
    saveStored(tableName || '', pending, applied);
  }, [tableName, pending, applied]);

  const addToPending = (change) => {
    const id = `p_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    setPending((prev) => [...prev, { ...change, id }]);
  };

  const removePending = (id) => {
    setPending((prev) => prev.filter((c) => c.id !== id));
  };

  const applyPending = async (item) => {
    try {
      if (item.type === 'add_column') {
        await api.post(`/settings/db/tables/${tableName}/columns`, {
          columnName: item.columnName,
          columnType: item.columnType,
          isNullable: item.isNullable !== false,
          columnDefault: item.columnDefault || undefined,
          afterColumn: item.afterColumn || undefined
        });
        setApplied((prev) => [...prev, { ...item, appliedAt: Date.now(), appliedId: item.id }]);
      } else if (item.type === 'drop_column') {
        await api.delete(`/settings/db/tables/${tableName}/columns/${item.columnName}`);
        setApplied((prev) => [...prev, { ...item, appliedAt: Date.now(), appliedId: item.id }]);
      }
      setPending((prev) => prev.filter((c) => c.id !== item.id));
      loadStructure();
      toast.success('Change applied');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Apply failed');
    }
  };

  const undoApplied = async (item) => {
    try {
      if (item.type === 'add_column') {
        await api.delete(`/settings/db/tables/${tableName}/columns/${item.columnName}`);
      } else if (item.type === 'drop_column' && item.definition) {
        const def = item.definition;
        await api.post(`/settings/db/tables/${tableName}/columns`, {
          columnName: def.COLUMN_NAME,
          columnType: def.COLUMN_TYPE || def.DATA_TYPE,
          isNullable: def.IS_NULLABLE === 'YES',
          columnDefault: def.COLUMN_DEFAULT || undefined,
          afterColumn: def.afterColumn || undefined
        });
      }
      setApplied((prev) => prev.filter((c) => c.appliedId !== item.appliedId));
      loadStructure();
      toast.success('Undone');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Undo failed');
    }
  };

  const handleAddColumnSubmit = (e) => {
    e.preventDefault();
    const name = (addForm.columnName || '').trim().replace(/[^a-zA-Z0-9_]/g, '');
    if (!name) {
      toast.error('Column name is required');
      return;
    }
    if (structure.some((c) => c.COLUMN_NAME === name) || pending.some((c) => c.type === 'add_column' && c.columnName === name)) {
      toast.error('Column already exists or is pending');
      return;
    }
    addToPending({
      type: 'add_column',
      columnName: name,
      columnType: addForm.columnType || 'VARCHAR(255)',
      isNullable: addForm.isNullable !== false,
      columnDefault: addForm.columnDefault || undefined
    });
    setAddForm({ columnName: '', columnType: 'VARCHAR(255)', isNullable: true, columnDefault: '' });
    setShowAddColumn(false);
  };

  const handleDropColumn = (col) => {
    if (pending.some((c) => c.type === 'drop_column' && c.columnName === col.COLUMN_NAME)) {
      toast.error('Already in pending');
      return;
    }
    const prevCol = structure[structure.indexOf(col) - 1];
    addToPending({
      type: 'drop_column',
      columnName: col.COLUMN_NAME,
      definition: {
        COLUMN_NAME: col.COLUMN_NAME,
        COLUMN_TYPE: col.COLUMN_TYPE,
        DATA_TYPE: col.DATA_TYPE,
        IS_NULLABLE: col.IS_NULLABLE,
        COLUMN_DEFAULT: col.COLUMN_DEFAULT,
        afterColumn: prevCol ? prevCol.COLUMN_NAME : undefined
      }
    });
  };

  if (user?.role !== 'ADMIN' && user?.role !== 'SUPER_ADMIN') return null;

  const currentColumnNames = structure.map((c) => c.COLUMN_NAME);
  const pendingDrops = pending.filter((c) => c.type === 'drop_column').map((c) => c.columnName);
  const displayStructure = structure.filter((c) => !pendingDrops.includes(c.COLUMN_NAME));

  return (
    <div>
      <ModulePageHeading
        resolveNav={resolveSettingsNav}
        title={tableName}
        infoTitle={tableName}
        infoChildren={<p>Changes are saved separately. Apply to run on DB; Undo to revert. Data is stored in this browser.</p>}
      />

      {/* Current structure */}
      <section className="bg-card rounded-lg border shadow-sm p-4 mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">Current columns</h2>
          <button
            type="button"
            onClick={() => setShowAddColumn(true)}
            className="flex items-center px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add column
          </button>
        </div>
        {loading ? (
          <p className="text-gray-500">Loading...</p>
        ) : (
          <div className="overflow-x-auto border rounded">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">COLUMN_NAME</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">COLUMN_TYPE</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">IS_NULLABLE</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">COLUMN_KEY</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">DEFAULT</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">EXTRA</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-700 w-20">Action</th>
                </tr>
              </thead>
              <tbody>
                {displayStructure.map((col, i) => (
                  <tr key={col.COLUMN_NAME} className="border-t">
                    <td className="px-3 py-2 font-medium">{col.COLUMN_NAME}</td>
                    <td className="px-3 py-2">{col.COLUMN_TYPE || col.DATA_TYPE}</td>
                    <td className="px-3 py-2">{col.IS_NULLABLE}</td>
                    <td className="px-3 py-2">{col.COLUMN_KEY || '-'}</td>
                    <td className="px-3 py-2">{col.COLUMN_DEFAULT ?? 'NULL'}</td>
                    <td className="px-3 py-2">{col.EXTRA || '-'}</td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => handleDropColumn(col)}
                        className="text-red-600 hover:text-red-800"
                        title="Drop column (add to pending)"
                      >
                        <Trash2 className="w-4 h-4 inline" />
                      </button>
                    </td>
                  </tr>
                ))}
                {pending.filter((c) => c.type === 'add_column').map((c) => (
                  <tr key={c.id} className="border-t bg-amber-50">
                    <td className="px-3 py-2 font-medium">{c.columnName}</td>
                    <td className="px-3 py-2">{c.columnType}</td>
                    <td className="px-3 py-2">{c.isNullable ? 'YES' : 'NO'}</td>
                    <td className="px-3 py-2">-</td>
                    <td className="px-3 py-2">{c.columnDefault || 'NULL'}</td>
                    <td className="px-3 py-2">(pending)</td>
                    <td className="px-3 py-2">-</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Pending changes (saved separately) */}
      <section className="bg-amber-50 rounded-lg border border-amber-200 p-4 mb-6">
        <h2 className="text-lg font-semibold mb-2 flex items-center">
          <Save className="w-5 h-5 mr-2" />
          Pending changes (saved in browser)
        </h2>
        {pending.length === 0 ? (
          <p className="text-sm text-gray-600">No pending changes.</p>
        ) : (
          <ul className="space-y-2">
            {pending.map((item) => (
              <li key={item.id} className="flex items-center justify-between py-2 border-b border-amber-200 last:border-0">
                <span className="text-sm">
                  {item.type === 'add_column' && <>Add column <strong>{item.columnName}</strong> ({item.columnType})</>}
                  {item.type === 'drop_column' && <>Drop column <strong>{item.columnName}</strong></>}
                </span>
                <div className="flex gap-2">
                  <button type="button" onClick={() => applyPending(item)} className="flex items-center px-2 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700">
                    <Check className="w-4 h-4 mr-1" /> Apply
                  </button>
                  <button type="button" onClick={() => removePending(item.id)} className="flex items-center px-2 py-1 border border-gray-300 rounded text-sm hover:bg-gray-100">
                    <X className="w-4 h-4 mr-1" /> Cancel
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Applied (can undo) */}
      <section className="bg-card rounded-lg border shadow-sm p-4">
        <h2 className="text-lg font-semibold mb-2 flex items-center">
          <Undo2 className="w-5 h-5 mr-2" />
          Applied (Undo to revert)
        </h2>
        {applied.length === 0 ? (
          <p className="text-sm text-gray-600">No applied changes in this session.</p>
        ) : (
          <ul className="space-y-2">
            {[...applied].reverse().map((item) => (
              <li key={item.appliedId || item.appliedAt} className="flex items-center justify-between py-2 border-b last:border-0">
                <span className="text-sm">
                  {item.type === 'add_column' && <>Added column <strong>{item.columnName}</strong></>}
                  {item.type === 'drop_column' && <>Dropped column <strong>{item.columnName}</strong></>}
                </span>
                <button type="button" onClick={() => undoApplied(item)} className="flex items-center px-2 py-1 bg-amber-600 text-white rounded text-sm hover:bg-amber-700">
                  <Undo2 className="w-4 h-4 mr-1" /> Undo
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Add column modal */}
      {showAddColumn && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowAddColumn(false)}>
          <div className="bg-card rounded-lg shadow-xl p-4 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-3">Add column (pending)</h3>
            <form onSubmit={handleAddColumnSubmit} className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Column name *</label>
                <input
                  type="text"
                  value={addForm.columnName}
                  onChange={(e) => setAddForm((p) => ({ ...p, columnName: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  placeholder="e.g. new_field"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Column type</label>
                <input
                  type="text"
                  value={addForm.columnType}
                  onChange={(e) => setAddForm((p) => ({ ...p, columnType: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  placeholder="VARCHAR(255), INT, TEXT, DATE, etc."
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isNullable"
                  checked={addForm.isNullable}
                  onChange={(e) => setAddForm((p) => ({ ...p, isNullable: e.target.checked }))}
                  className="rounded border-gray-300"
                />
                <label htmlFor="isNullable" className="text-sm text-gray-700">Nullable</label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Default (optional)</label>
                <input
                  type="text"
                  value={addForm.columnDefault}
                  onChange={(e) => setAddForm((p) => ({ ...p, columnDefault: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  placeholder="NULL, 0, CURRENT_TIMESTAMP, etc."
                />
              </div>
              <div className="flex gap-2 pt-2">
                <button type="submit" className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700">Add to pending</button>
                <button type="button" onClick={() => setShowAddColumn(false)} className="px-4 py-2 border rounded hover:bg-gray-50">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
